import type { LandingBlock, LandingPage } from "./types";

/**
 * How each published landing section is laid out, as measured off the live
 * site. The section counterpart of `heroLayout.ts`, and kept out of the CMS
 * for the same reason: an editor writes a section's words and picks its
 * picture, while "this box is mint and 400 wide", "this heading sits left" and
 * "this band closes short" are facts about the published design. They change
 * when the design does, a wrong one is a layout defect rather than a content
 * mistake, and a diff is where such a change should show up.
 *
 * A page is a list of blocks, so a layout is keyed by the block's kind and its
 * place among blocks of that kind: `split[1]` is the page's second split. That
 * survives every content edit, and survives an editor adding a block of a
 * different kind; a new split, or a reordering, is a design change and comes
 * here with it.
 */
export type SectionLayout = {
  /**
   * `.container_new`'s padding, authored per section on the published pages.
   * `break` is `.padding-bottom`'s short close; `well` is the rotating page's
   * grey FAQ well, which the feature panels open inside, and `tail` the
   * control well that follows them there. The rest name the published
   * wrappers whose margins and paddings a section is made of, as
   * `LandingSection`'s padding table states them.
   */
  pad?:
    | "none"
    | "top"
    | "bottom"
    | "both"
    | "break"
    | "well"
    | "tail"
    | "topBreak"
    | "benefits"
    | "benefitsBreak"
    | "priced"
    | "strip"
    | "doubleBreak"
    | "cloneable"
    | "bothTail";
  /**
   * `.pricing_plan_wrap.is-use-case`: the plan wrap opens 64 under its
   * section, and the band behind the cards does not. `wide` is the plain
   * `.pricing_plan_wrap`, 128 under it and 80 below the wide step.
   */
  planLead?: true | "wide";
  /**
   * The section's own id where a page's controls point at it by another name:
   * the campaign pages' "Try Now" goes to `#pricing-section`.
   */
  anchor?: string;
  /**
   * `.eth-sourc_contain`: the block stands in the ethics page's flowing
   * column rather than in a section of its own: on the 1100 measure, its
   * heading 32 over its body (16 on a phone), and its right edge inset 36
   * below the wide step, 30 below the card step, 16 on a phone. `flow` is how
   * far under the block above it stands: the wrap's 82 (64, 48) between
   * contains, or the contain's own 32 (16) where it continues the one above;
   * the last block closes the wrap on 100 (64 on a phone).
   */
  contain?: true;
  flow?: "wrap" | "contain";
  flowEnd?: true;
  /** `cards`: `.eth-sourc_box`, the title and lines beside a column of icon cards, on the washed panel. */
  aside?: true;
  /**
   * `prose`: `.eth-table-title_wrap`, the heading centred and, for `intro`,
   * its line centred under it at the small size; `titleBody` sets the lines
   * under the wrap at body size, full width.
   */
  proseShape?: "intro" | "titleBody";
  /** `matrix`: `.eth-table-body_wrap`, the footnotes side by side rather than stacked. */
  notesRow?: true;
  /** `matrix`: `.resi-table_content`, the tier column with its ticked grants beside two text columns. */
  graded?: true;
  /**
   * `matrix`: the "currently on this page" pill published before the product's
   * name rather than after it, which shows on a phone, where it stops floating
   * over the cell and stacks in it.
   */
  markerFirst?: true;
  /** Sets the line under the heading a size down, in the muted grey. */
  bodyTone?: "small";
  /** Most published sections centre their heading; a few set it left. */
  headingAlign?: "left" | "center";
  /** The box the section is set on, where the published page gives it one. */
  ground?: "wash" | "surface" | "mint" | "lilac";
  /** `.link`: the inline links set in the page's purple rather than its ink. */
  purpleLinks?: true;
  /** `.link-hover`: the lead's links turn the accent's dark step and underline under the pointer. */
  hoverLinks?: true;
  /** `split`: `.button_new.is_fixed`, the control 200 wide down to the tiny step. */
  fixedControl?: true;
  /** `whyWebshare`: published in `.c-cloneable` on the page's white rather than on its grey section. */
  bare?: true;
  /** `ppcHero`: `.ppc-visual_box`, the green box with its own wordmark and the tiles inside it. */
  box?: true;
  /**
   * `logoMarquee`: `.container_new.is-logos` set absolutely at the foot of the
   * section above, over its bottom edge, with the page's `.sontact-spacer`
   * under it.
   */
  overlay?: true;
  /** `ppcHero`: `.hero-visual_list.is-hide`, the box's tiles dropped below the card step. */
  tilesHidden?: true;
  /** `ppcHero`: `.hero-ppc-title_list`, the title on 81ch rather than 69. */
  wideTitle?: true;
  /** `customerQuote`: `.trust-ppc_wrap`, the light card, rather than the dark band. */
  quoteCard?: true;
  /** `issuePanels`: per panel, `.ppx-problem_wrap` on its red wash or `.ppx-solution_wrap` dark. */
  issueTones?: ReadonlyArray<"problem" | "solution">;
  /** `cards/feature`: `.c-cloneable.is-ppc-prod`, the product rows' boxes in the cloneable's stack. */
  productRows?: true;
  /** `issuePanels`: published in `.c-cloneable`, so the panels stack as the page scrolls. */
  stacked?: true;
  /** `cards/plain`: `.ppc-why_list`, the cards wrapped and centred rather than gridded. */
  whyList?: true;
  /**
   * `tileGallery`: `.use-case-pr_wrap`, the product pages' use-case cards
   * copied onto the page with copy of its own, rather than the CMS list.
   */
  productList?: true;
  /**
   * `stats` (numbers): `.why-web_item` published straight in its section with
   * no container, so the band runs to the page's edges and its own measures
   * centre it.
   */
  bleed?: true;
  /** A fixed square the illustration is contained in; the same at every step, so a number and not a breakpoint. */
  imageBox?: number;
  /** The illustration on the left. */
  reversed?: true;
  /** Below the wide step the panel stacks; the gap it closes to is 16 unless stated. */
  stackGap?: 32;
  /** Stacked, the art leads even though the copy leads in the row. */
  stackArtFirst?: true;
  /** `.table-pricing_top.is-static-resi`: the 800px intro measure rather than 640. */
  wideIntro?: true;
  /** The band under the table stands in the section directly, so below 768 it takes no second gap. */
  bareTail?: true;
  /**
   * `cards/feature`: panels drawn in a column narrower than the container.
   * `block` is a band published in a bare `.w-layout-blockcontainer`: 940
   * wide, 728 below the wide step, the page's width below the card step.
   */
  frame?: "narrow" | "block";
  /** `.container_new` inside `.container_new`: the band inset by a second gutter. */
  nestedFrame?: true;
  /** How many cards across, where the published grid does not take its kind's default. */
  across?: number;
  /** Run the feature-panel palette to its end, so the last band is peach. */
  panelsFromEnd?: true;
  /** The use-case grid titles a size tighter than every other benefits grid. */
  tightCardTitle?: true;
  /** `stats`: `.h4_new`, the campaign strips' figures at 24 where every product strip sets `.feature-metric_title`'s 20. */
  largeFigures?: true;
  /** `plans`: a card grid set on the page's own white, with no `pricing_bg_wrap` band behind it. */
  noBand?: true;
  /**
   * `plans`: `.pricing_button_secondary.is-primary`, which no published rule
   * styles: every card's control is the outline, the set-apart cards' too.
   */
  outlineControls?: true;
  /**
   * `cards` (product): `.product-card_con`, the campaign pages' priced cards on
   * a gradient panel, where the product pages draw `.loc-prosuct_item`.
   */
  cardPanel?: true;
  /** `split` (extension): `.chrome_ext-wrap` around the card, its own 80 over and 32 under. */
  ownBreak?: true;
  /** `cards` (product panel): the price chip publishes an empty note slot, which still takes the chip's 8. */
  noteSlot?: true;
  /**
   * `developer`: the marks a row draws larger (`.marquee-logos_image.is-bigger`),
   * as [row, position] pairs.
   */
  wideLogos?: ReadonlyArray<readonly [number, number]>;
  /**
   * `plans`: `.pricing_plan_wrap` published inside `.pricing_plan_title_wrap`,
   * so the heading's own 12 stands between its line and the controls too, and
   * the cards take its centred text.
   */
  plansInHead?: true;
  /**
   * `.alter-features_list`: the comparison pages' two-up list, which names two
   * rows below the wide step where `.benefits_list` names two and then four.
   * `.benefits_list.is-6` names two as well.
   */
  pairList?: true;
  /**
   * `.benefits_list.is-6` and `.is-use-case`: two across from the wide step
   * down to the tiny one, where the plain list goes to one at the card step.
   */
  holdPairs?: true;
  /**
   * `cards/feature` whose copy is published as rich text: the page's own body
   * size at every width, each paragraph closing under itself, the column's
   * own width. Absent, the copy is `.feature_card-body`.
   */
  richCopy?: true;
  /** `linkGrid`: per group, in order. The category bands alternate which side the picture takes. */
  groupLayout?: ReadonlyArray<GroupLayout | undefined>;
  /** `heading`: the Trustpilot rating strip six pages draw with the heading. */
  trustStrip?: true;
  /**
   * `ctaBand`: which of the published bands closes the page. `plain` is the
   * bare register well, `alternative` the dark panel that states its heading at
   * hero size, `pastel` the YouTube page's gradient box, `ppc` the paid-search
   * pages' `.ppc-cta_wrap`; absent, the navy band.
   */
  ctaBand?: "plain" | "alternative" | "pastel" | "ppc";
  /**
   * `ctaBand`: the affiliate page states the navy band's heading at hero size
   * in a plain division, where the referral page's is an `h2` at the band's own.
   */
  heroTitle?: true;
  /**
   * `split`: `.chrome_box.is-white` and `.is-black`, the extension page's two
   * boxed sections: the white card with the picture beside the copy, and the
   * dark card that numbers its lines and carries its art behind them.
   */
  chromeBox?: "white" | "black";
};

export type GroupLayout = {
  reversed?: true;
  bodyTone?: "small";
  /** `tile`: `.loc-resi_toptst_wrap` without `.is-full`, the heading and its line on a 50ch measure. */
  narrowHead?: true;
};

/** One block kind's sections in order; a hole is a section laid out like its kind's default. */
type PageLayout = Partial<
  Record<LandingBlock["kind"], ReadonlyArray<SectionLayout | undefined>>
>;

const SMALL: SectionLayout = { bodyTone: "small" };
const SURFACE: SectionLayout = { ground: "surface" };
const MINT_BOX: SectionLayout = { ground: "mint", imageBox: 400 };
const LILAC_BOX: SectionLayout = {
  ground: "lilac",
  imageBox: 400,
  stackGap: 32,
  stackArtFirst: true,
};
const SHORT_CLOSE: SectionLayout = { pad: "break" };
const NO_CLOSE: SectionLayout = { pad: "none" };
const LEFT: SectionLayout = { headingAlign: "left" };
const TRUST: SectionLayout = { trustStrip: true };
const ALT_BAND: SectionLayout = { ctaBand: "alternative" };

/**
 * What a family draws, measured: the four competitor pages agree with each
 * other exactly, the eleven use-case pages agree exactly, and the four
 * alternative pages agree wherever they have the same sections (two of them
 * carry a fifth split, which the fifth entry is for).
 */
const BY_GROUP: Partial<Record<LandingPage["group"], PageLayout>> = {
  alternative: {
    split: [
      { ground: "wash", imageBox: 372 },
      SURFACE,
      SURFACE,
      { ground: "surface", reversed: true },
      SURFACE,
    ],
    // The rundown, the price cards, then the two-up features list.
    cards: [SMALL, undefined, { pairList: true }],
    matrix: [SMALL],
    heading: [SMALL, { bodyTone: "small", trustStrip: true }, SMALL],
    ctaBand: [ALT_BAND],
  },
  competitor: {
    stats: [SMALL],
    cards: [{ bodyTone: "small", headingAlign: "left" }],
    plans: [{ pad: "top", bodyTone: "small" }],
  },
  useCase: {
    cards: [
      { tightCardTitle: true, pad: "break", holdPairs: true },
      { richCopy: true },
    ],
    plans: [{ pad: "none", planLead: true }],
  },
};

/**
 * The partner campaign pages under `/l/`, which share one published template:
 * the figures strip closing short, the locations with no close of their own,
 * the numbers band opening the full break, the pricing band that the hero's
 * "Try Now" points at, then the extension and the review band. They differ in
 * three things only: whether the locations heading keeps its 50ch measure
 * (`.is-full` drops it), whether the cards' controls are all outlines, and
 * whether the plans were published inside the heading's wrap.
 */
function partner({
  fullHead,
  outlined,
  nested,
}: { fullHead?: true; outlined?: true; nested?: true } = {}): PageLayout {
  return {
    stats: [{ largeFigures: true }, { pad: "top" }],
    linkGrid: [
      {
        pad: "none",
        groupLayout: [
          { bodyTone: "small", ...(fullHead ? {} : { narrowHead: true }) },
        ],
      },
    ],
    plans: [
      {
        pad: "break",
        anchor: "pricing-section",
        noBand: true,
        planLead: "wide",
        ...(outlined ? { outlineControls: true } : {}),
        ...(nested ? { plansInHead: true } : {}),
      },
    ],
    split: [{ pad: "top" }],
    heading: [{ trustStrip: true, pad: "both" }],
  };
}

/** `.testimonial_wrap-trust-pilot`: the review heading over the Trustpilot strip. */
const TRUST_STRIP: SectionLayout = { trustStrip: true, pad: "both" };
/** `.main_header_wrap.is-table`: the washed box, a 480 square beside the copy, 32 apart stacked. */
const WASH_BOX: SectionLayout = {
  ground: "wash",
  imageBox: 480,
  pad: "break",
  stackGap: 32,
};

const PARTNER = partner();
const PARTNER_OUTLINED = partner({ outlined: true });
const PARTNER_FULL = partner({ fullHead: true });
const PARTNER_FULL_OUTLINED = partner({ fullHead: true, outlined: true });

/** What a page draws over its family, kind by kind. */
const BY_PATH: Record<string, PageLayout> = {
  "/l/blackdown": PARTNER_FULL_OUTLINED,
  "/l/cvamp": PARTNER,
  "/l/cybernews": PARTNER_OUTLINED,
  "/l/designrush": partner({ fullHead: true, outlined: true, nested: true }),
  "/l/dginnovations": PARTNER_OUTLINED,
  "/l/jdepoix": PARTNER_FULL,
  "/l/privateproxyreviews": PARTNER_FULL,
  "/l/promo": PARTNER_FULL,
  "/l/promo15": PARTNER,
  "/l/proxies": PARTNER_FULL,
  "/l/proxyway": PARTNER,
  "/l/vpncentral": partner({ outlined: true, nested: true }),
  // The product variants, each read off its published sections.
  "/l/dedicated-isp-proxies": {
    stats: [{ pad: "strip" }, { ground: "surface", frame: "block" }],
    cards: [
      { pad: "benefitsBreak" },
      { cardPanel: true, noteSlot: true, pad: "priced" },
    ],
  },
  "/l/dedicated-proxy": {
    stats: [{ pad: "strip" }],
    cards: [{ pad: "benefitsBreak" }, { pad: "bottom" }],
    whyWebshare: [{ bare: true, pad: "cloneable" }],
    heading: [{ ...TRUST_STRIP, pad: "none", ground: "surface" }],
  },
  "/l/residential-proxy": {
    stats: [{ pad: "none", bleed: true }],
    split: [WASH_BOX],
    cards: [
      { pad: "benefits", holdPairs: true, pairList: true },
      { cardPanel: true, pad: "priced" },
    ],
    linkGrid: [{ pad: "none" }],
    heading: [{ ...TRUST_STRIP, pad: "doubleBreak" }],
  },
  "/l/rotating-residential-proxy": {
    split: [WASH_BOX],
    linkGrid: [{ pad: "none" }],
    cards: [
      { pad: "top", holdPairs: true, pairList: true },
      { cardPanel: true, pad: "priced" },
    ],
    developer: [{ pad: "bothTail" }],
  },
  "/l/static-residential-proxies": {
    stats: [{ pad: "strip" }],
    cards: [{ pad: "benefits" }, { cardPanel: true }],
    split: [{ ownBreak: true }],
    useCaseRow: [{ pad: "break" }],
    heading: [{ ...TRUST_STRIP, pad: "break" }],
  },
  // The paid-search pages, each read off its published sections.
  "/l/best-proxies": {
    ppcHero: [{ box: true, tilesHidden: true }],
    customerQuote: [undefined, { pad: "both" }],
    tileGallery: [{ productList: true }],
    ctaBand: [{ ctaBand: "ppc" }],
  },
  "/l/best-proxy": {
    ppcHero: [{ box: true }],
    tileGallery: [{ productList: true }],
    ctaBand: [{ ctaBand: "ppc" }],
  },
  "/l/premium-proxy": {
    issuePanels: [
      { pad: "top", stacked: true, issueTones: ["problem", "solution"] },
      { issueTones: ["solution"] },
    ],
    cards: [{ productRows: true }, { whyList: true, pad: "topBreak" }],
  },
  "/l/proxy-start": {
    ppcHero: [{ wideTitle: true }],
    customerQuote: [{ quoteCard: true }, { quoteCard: true, pad: "break" }],
    stats: [{ pad: "break" }],
    heading: [{ ...TRUST_STRIP, pad: "break" }],
    linkGrid: [{ pad: "break", groupLayout: [SMALL] }],
    issuePanels: [{ pad: "top", issueTones: ["solution"] }],
  },
  "/enterprise-form": {
    logoMarquee: [{ overlay: true }],
  },
  "/affiliate-program": {
    checkList: [{ headingAlign: "left", pad: "break" }],
    ctaBand: [{ heroTitle: true }],
  },
  "/brightdata-alternative": { stats: [SMALL] },
  "/chrome-proxy-extension": {
    split: [{ chromeBox: "white" }, { chromeBox: "black" }],
    cards: [LEFT],
    linkGrid: [{ groupLayout: [SMALL], pad: "none" }],
    useCaseRow: [{ pad: "both" }],
  },
  "/dedicated-datacenter-proxy": {
    split: [MINT_BOX],
    matrix: [{ markerFirst: true }],
    cards: [SHORT_CLOSE],
    plans: [SHORT_CLOSE],
    useCaseRow: [SHORT_CLOSE],
  },
  "/dedicated-proxy": {
    stats: [{ nestedFrame: true }],
    // The box closes on the text block the published section nests under it,
    // and the comparison opens on a break of its own.
    split: [
      { ...MINT_BOX, pad: "none" },
      { headingAlign: "center", ground: "surface", purpleLinks: true },
    ],
    // `.benefits_wrap`'s own 100 over the band and the section's 20 under it.
    cards: [{ pad: "benefits", hoverLinks: true }],
    matrix: [{ pad: "top" }],
    plans: [{ pad: "both" }],
    useCaseRow: [SHORT_CLOSE],
  },
  "/dedicated-static-residential-proxy": {
    split: [LILAC_BOX],
    cards: [SHORT_CLOSE],
    plans: [SHORT_CLOSE],
    useCaseRow: [SHORT_CLOSE],
  },
  "/integrations": {
    heading: [TRUST],
    linkGrid: [
      undefined,
      undefined,
      { groupLayout: [{ reversed: true }] },
      undefined,
      { groupLayout: [{ reversed: true }] },
    ],
  },
  "/private-datacenter-proxy": {
    split: [MINT_BOX],
    cards: [SHORT_CLOSE],
    // The table the page's "See Pricing" points at, by the published id.
    plans: [{ ...SHORT_CLOSE, anchor: "ded-priv-pricing-list" }],
    useCaseRow: [SHORT_CLOSE],
  },
  "/private-proxy": {
    split: [
      { ground: "wash", imageBox: 480, reversed: true, pad: "break" },
      { ground: "wash", imageBox: 480, pad: "break" },
      { ground: "wash", imageBox: 480, reversed: true, pad: "break" },
    ],
    plans: [{ pad: "both" }],
    cards: [{ ...SHORT_CLOSE, hoverLinks: true }, { panelsFromEnd: true }],
    useCaseRow: [NO_CLOSE],
  },
  "/private-static-residential-proxy": {
    split: [LILAC_BOX],
    cards: [SHORT_CLOSE],
    plans: [SHORT_CLOSE],
    useCaseRow: [SHORT_CLOSE],
  },
  "/proxy-server": {
    heading: [TRUST, SMALL],
    linkGrid: [NO_CLOSE],
    plans: [{ pad: "top", bareTail: true }],
    cards: [{ hoverLinks: true }, { bodyTone: "small", pad: "top" }],
  },
  "/rayobyte-alternative": { stats: [SMALL] },
  "/referral-program": {
    checkList: [{ headingAlign: "left", pad: "break" }],
  },
  "/residential-proxy": {
    linkGrid: [NO_CLOSE],
    plans: [{ pad: "top" }],
    code: [SMALL],
    cards: [{ ...NO_CLOSE, holdPairs: true, pairList: true }],
    split: [
      { ground: "wash", imageBox: 480, stackGap: 32, fixedControl: true },
    ],
  },
  "/rotating-proxy": {
    split: [{ ground: "wash", imageBox: 480, reversed: true, pad: "break" }],
    ctaBand: [{ ctaBand: "plain", pad: "tail" }],
    plans: [{ pad: "both", bareTail: true }],
    cards: [{ frame: "narrow", pad: "well" }],
    faq: [NO_CLOSE],
  },
  "/static-residential-proxy": {
    cards: [
      NO_CLOSE,
      { bodyTone: "small", pad: "top" },
      { panelsFromEnd: true },
    ],
    plans: [{ pad: "top", wideIntro: true }],
    useCaseRow: [SHORT_CLOSE],
  },
  "/webshares-ethically-sourced-residential-proxies": {
    cards: [
      { contain: true, flow: "contain", aside: true },
      { contain: true, flow: "wrap", across: 2 },
    ],
    prose: [
      { contain: true, flow: "wrap", proseShape: "intro" },
      { contain: true, flow: "wrap", proseShape: "titleBody", flowEnd: true },
    ],
    matrix: [{ contain: true, flow: "contain", notesRow: true, graded: true }],
    quote: [{ contain: true, flow: "wrap" }],
  },
  "/youtube-proxy": {
    cards: [{ headingAlign: "center" }, { across: 3 }],
    ctaBand: [{ ctaBand: "pastel" }],
  },
};

/**
 * The layout of a page's `ordinal`th block of `kind`: the page's own where it
 * states one for that kind, else its family's, else the kind's default.
 */
export function sectionLayout(
  page: { path: string; group: LandingPage["group"] },
  kind: LandingBlock["kind"],
  ordinal: number
): SectionLayout {
  const sections = BY_PATH[page.path]?.[kind] ?? BY_GROUP[page.group]?.[kind];
  return sections?.[ordinal] ?? {};
}

/** A block as a renderer sees it: its content, and the layout its page gives it. */
export type Laid<K extends LandingBlock["kind"]> = Extract<
  LandingBlock,
  { kind: K }
> &
  SectionLayout;

/**
 * The page's blocks with their layout on them, in order. Done once for the
 * page rather than per block, so that a block and the neighbours it looks at
 * are the same objects.
 */
export function layOut(
  page: LandingPage
): ReadonlyArray<Laid<LandingBlock["kind"]>> {
  const seen: Partial<Record<LandingBlock["kind"], number>> = {};
  return page.blocks.map((block) => {
    const ordinal = seen[block.kind] ?? 0;
    seen[block.kind] = ordinal + 1;
    return { ...block, ...sectionLayout(page, block.kind, ordinal) };
  });
}
